/**
 * Upload slots per batch. A 100-file ZIP must not open 100 connections at
 * once: the browser queues them anyway (about six per host), every one shows
 * "uploading" while it waits, and the presign calls all hit the API together.
 */
export const MAX_PARALLEL_UPLOADS = 4

/**
 * `Promise.all(items.map(fn))` with at most `limit` calls in flight. Items
 * start in order and results come back in input order. A rejection rejects the
 * whole call, like `Promise.all` — callers that must not stop early catch
 * inside `fn`.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0

  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const index = next++
      results[index] = await fn(items[index]!, index)
    }
  }

  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker)
  await Promise.all(workers)
  return results
}
